import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Chip,
  Button,
  Stack,
  Slider,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Alert,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  MusicNote as MusicNoteIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Share as ShareIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import api from '../services/api';

// ── Vibe / occasion presets ──────────────────────────────────────────────
const VIBE_PRESETS = [
  { label: 'Dinner Party', value: 'upbeat dinner-party energy, communal and warm but not background — guests engaged, mid-tempo' },
  { label: 'Cocktail Hour', value: 'sophisticated cocktail-hour groove, jazzy or lounge influence, smooth and conversational' },
  { label: 'Workout', value: 'high-energy workout track, driving tempo, motivating and powerful' },
  { label: 'Focus / Work', value: 'focused work music, instrumental-leaning, steady rhythm, no major distractions' },
  { label: 'Dance Party', value: 'full dance-floor energy, four-on-the-floor groove, danceable and infectious' },
  { label: 'Sunday Morning', value: 'mellow Sunday-morning vibe, relaxed tempo, warm and easy' },
  { label: 'Anniversary', value: 'celebratory anniversary anthem, romantic and triumphant' },
  { label: 'Corporate Event', value: 'polished corporate event track, uplifting and tasteful, professional energy' },
  { label: 'Tribute / Roast', value: 'larger-than-life tribute song, swaggering and fun, a bit theatrical' },
];

// ── Tempo options ────────────────────────────────────────────────────────
const TEMPO_OPTIONS = [
  { label: 'Slow', value: 'slow tempo around 70-85 BPM, laid-back and unhurried' },
  { label: 'Mid', value: 'mid tempo around 95-110 BPM, balanced energy' },
  { label: 'Upbeat', value: 'upbeat tempo around 115-135 BPM, lively and driving' },
];

// ── Length options ───────────────────────────────────────────────────────
const LENGTH_OPTIONS = [
  { label: '1 min', value: 60 },
  { label: '90 sec', value: 90 },
  { label: '2 min', value: 120 },
  { label: '3 min', value: 180 },
];

// ── Suggested starter artists (just to get them rolling) ─────────────────
const ARTIST_SUGGESTIONS = [
  'Elton John', 'Billy Joel', 'The Beatles', 'Bob Marley',
  'Rolling Stones', 'Janis Joplin', 'The Doors', 'Eminem',
  'The Supremes', 'Johnny Cash', 'Adele', 'Coldplay',
];

export default function Soundcheck() {
  // Auth context — Soundcheck works with OR without an ACS login. When a
  // user is logged in we render inside the normal Layout (see App.js); the
  // public shell wrapper below only kicks in for anonymous visitors so the
  // page looks like a proper public landing rather than a bare form.
  const { user } = useAuth();
  const isAnon = !user;

  // Form state
  const [title, setTitle] = useState('');
  const [artists, setArtists] = useState([]);
  const [artistInput, setArtistInput] = useState('');
  const [vibe, setVibe] = useState('');
  const [vibePreset, setVibePreset] = useState('');
  const [about, setAbout] = useState('');
  const [tempo, setTempo] = useState(TEMPO_OPTIONS[1].value);
  const [duration, setDuration] = useState(120);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [progressMsg, setProgressMsg] = useState('');

  // Library
  const [tracks, setTracks] = useState([]);
  const [expanded, setExpanded] = useState({}); // uuid -> bool

  const loadTracks = useCallback(async () => {
    try {
      const r = await api.get('/soundcheck');
      setTracks(r.data.tracks || []);
    } catch (e) {
      console.error('Failed to load tracks', e);
    }
  }, []);

  useEffect(() => { loadTracks(); }, [loadTracks]);

  const addArtist = (name) => {
    const v = String(name || '').trim();
    if (!v) return;
    if (artists.includes(v)) return;
    if (artists.length >= 5) return;
    setArtists([...artists, v]);
    setArtistInput('');
  };

  const removeArtist = (name) => {
    setArtists(artists.filter((a) => a !== name));
  };

  const pickVibe = (preset) => {
    setVibePreset(preset.label);
    setVibe(preset.value);
  };

  const generate = async () => {
    setError('');
    if (!title.trim()) {
      setError('Give the song a title.');
      return;
    }
    if (artists.length === 0) {
      setError('Add at least one artist influence so we know the vibe.');
      return;
    }

    setGenerating(true);
    setProgressMsg('Composing lyrics…');

    // Heuristic progress messaging while we wait — generation is synchronous,
    // typically 60–120 sec.
    const stages = [
      { at: 0,    msg: 'Composing lyrics…' },
      { at: 8,    msg: 'Designing the musical style…' },
      { at: 18,   msg: 'Sending to the music engine…' },
      { at: 35,   msg: 'Recording vocals…' },
      { at: 60,   msg: 'Mixing the track…' },
      { at: 90,   msg: 'Almost there — final pass…' },
    ];
    const startedAt = Date.now();
    const interval = setInterval(() => {
      const sec = Math.floor((Date.now() - startedAt) / 1000);
      const stage = stages.filter((s) => s.at <= sec).pop();
      if (stage) setProgressMsg(stage.msg);
    }, 2000);

    try {
      const r = await api.post('/soundcheck/generate', {
        title: title.trim(),
        artists,
        vibe,
        about,
        tempo,
        duration_seconds: duration,
      });
      clearInterval(interval);
      setProgressMsg('');
      setTracks([r.data, ...tracks]);
      // Clear form (keep artist list to make follow-up generations easy)
      setTitle('');
      setAbout('');
      setExpanded({ ...expanded, [r.data.uuid]: true });
    } catch (e) {
      clearInterval(interval);
      setProgressMsg('');
      setError(e?.response?.data?.error || e.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const deleteTrack = async (uuid) => {
    if (!window.confirm('Delete this track? This cannot be undone.')) return;
    try {
      await api.delete(`/soundcheck/${uuid}`);
      setTracks(tracks.filter((t) => t.uuid !== uuid));
    } catch (e) {
      setError('Failed to delete: ' + (e?.response?.data?.error || e.message));
    }
  };

  const toggleExpand = (uuid) => {
    setExpanded({ ...expanded, [uuid]: !expanded[uuid] });
  };

  // ── Share dialog state ─────────────────────────────────────────────────
  const [shareTrack, setShareTrack] = useState(null); // full track object
  const [shareSaving, setShareSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const openShareDialog = (track) => {
    setCopied(false);
    setShareTrack(track);
  };
  const closeShareDialog = () => {
    setShareTrack(null);
    setCopied(false);
  };

  const buildShareUrl = (uuid) =>
    `${window.location.origin}/soundcheck/share/${uuid}`;

  const toggleShare = async (nextValue) => {
    if (!shareTrack) return;
    setShareSaving(true);
    try {
      const r = await api.post(`/soundcheck/${shareTrack.uuid}/share`, {
        is_public: nextValue,
      });
      // Update local state
      const updated = { ...shareTrack, is_public: r.data.is_public, shared_at: r.data.shared_at };
      setShareTrack(updated);
      setTracks(tracks.map((t) => (t.uuid === shareTrack.uuid ? updated : t)));
    } catch (e) {
      setError('Failed to update share: ' + (e?.response?.data?.error || e.message));
    } finally {
      setShareSaving(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareTrack) return;
    const url = buildShareUrl(shareTrack.uuid);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback — select the input
      const el = document.getElementById('soundcheck-share-url-input');
      if (el) { el.focus(); el.select(); }
    }
  };

  const content = (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <MusicNoteIcon sx={{ fontSize: 36, color: '#0D2C54' }} />
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap">
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D2C54' }}>
              Soundcheck
            </Typography>
            <Typography
              variant="overline"
              sx={{
                color: '#0D2C54',
                opacity: 0.65,
                letterSpacing: 1.2,
                fontWeight: 600,
                lineHeight: 1,
              }}
            >
              ACS Interactive — a preview of what's next
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Custom AI-generated music — pick your influences, tell us the vibe,
            and we'll produce an MP3 ready for the system.
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {/* Form */}
      <Paper sx={{ p: 3, mb: 4, border: '1px solid #e0e0e0' }} elevation={0}>
        <Stack spacing={3}>
          {/* Title */}
          <TextField
            label="Song title"
            placeholder="e.g. Robert Kaufman, or Friday Night Vibes"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            disabled={generating}
            helperText="If the title is a person's name, it'll show up in the chorus."
          />

          {/* Artists */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Artist influences <span style={{ color: '#888', fontWeight: 400 }}>— up to 5</span>
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
              {artists.map((a) => (
                <Chip
                  key={a}
                  label={a}
                  onDelete={() => removeArtist(a)}
                  disabled={generating}
                  sx={{ bgcolor: '#0D2C54', color: '#fff', '& .MuiChip-deleteIcon': { color: '#fff' } }}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                placeholder="Type an artist and press Enter"
                value={artistInput}
                onChange={(e) => setArtistInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addArtist(artistInput);
                  }
                }}
                size="small"
                disabled={generating || artists.length >= 5}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                onClick={() => addArtist(artistInput)}
                disabled={generating || artists.length >= 5 || !artistInput.trim()}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Stack>
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {ARTIST_SUGGESTIONS.filter((s) => !artists.includes(s)).slice(0, 8).map((s) => (
                <Chip
                  key={s}
                  label={s}
                  size="small"
                  variant="outlined"
                  onClick={() => addArtist(s)}
                  disabled={generating || artists.length >= 5}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>

          {/* Vibe / Occasion */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Vibe / occasion
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              {VIBE_PRESETS.map((p) => (
                <Chip
                  key={p.label}
                  label={p.label}
                  onClick={() => pickVibe(p)}
                  disabled={generating}
                  variant={vibePreset === p.label ? 'filled' : 'outlined'}
                  sx={vibePreset === p.label
                    ? { bgcolor: '#FF6B35', color: '#0D2C54', fontWeight: 600 }
                    : { cursor: 'pointer' }}
                />
              ))}
            </Stack>
            <TextField
              label="Or describe the vibe in your own words"
              placeholder="e.g. summer rooftop dinner with about 8 close friends, warm but lively"
              value={vibe}
              onChange={(e) => { setVibe(e.target.value); setVibePreset(''); }}
              fullWidth
              multiline
              rows={2}
              disabled={generating}
            />
          </Box>

          {/* About */}
          <TextField
            label="What's the song about? (optional)"
            placeholder="e.g. The story of ACS — founded 1976 by Robert Kaufman, top luxury integrator in NY, 100+ team, three offices"
            value={about}
            onChange={(e) => setAbout(e.target.value)}
            fullWidth
            multiline
            rows={3}
            disabled={generating}
            helperText="Names, milestones, places — anything you want woven into the lyrics."
          />

          {/* Tempo + Length row */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Tempo</Typography>
              <Stack direction="row" spacing={1}>
                {TEMPO_OPTIONS.map((t) => (
                  <Chip
                    key={t.label}
                    label={t.label}
                    onClick={() => setTempo(t.value)}
                    disabled={generating}
                    variant={tempo === t.value ? 'filled' : 'outlined'}
                    sx={tempo === t.value
                      ? { bgcolor: '#0D2C54', color: '#fff', fontWeight: 600 }
                      : { cursor: 'pointer' }}
                  />
                ))}
              </Stack>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Length</Typography>
              <Stack direction="row" spacing={1}>
                {LENGTH_OPTIONS.map((l) => (
                  <Chip
                    key={l.label}
                    label={l.label}
                    onClick={() => setDuration(l.value)}
                    disabled={generating}
                    variant={duration === l.value ? 'filled' : 'outlined'}
                    sx={duration === l.value
                      ? { bgcolor: '#0D2C54', color: '#fff', fontWeight: 600 }
                      : { cursor: 'pointer' }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>

          {/* Errors */}
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Generate button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={generate}
              disabled={generating}
              startIcon={generating ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <MusicNoteIcon />}
              sx={{
                bgcolor: '#0D2C54',
                fontWeight: 600,
                px: 4,
                '&:hover': { bgcolor: '#1A5490' },
              }}
            >
              {generating ? 'Generating…' : 'Generate Track'}
            </Button>
            {generating && (
              <Typography variant="body2" color="text.secondary">
                {progressMsg} (typically 60–120 seconds)
              </Typography>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* Library */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0D2C54' }}>
          Your Tracks
        </Typography>
        <IconButton onClick={loadTracks} size="small">
          <RefreshIcon />
        </IconButton>
      </Stack>

      {tracks.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc' }}>
          <Typography color="text.secondary">
            No tracks yet. Fill out the form above and hit Generate.
          </Typography>
        </Paper>
      )}

      <Stack spacing={2}>
        {tracks.map((t) => (
          <Paper key={t.uuid} sx={{ p: 2, border: '1px solid #e0e0e0' }} elevation={0}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0D2C54' }}>
                    {t.title}
                  </Typography>
                  {t.status === 'generating' && (
                    <Chip size="small" label="Generating…" color="warning" />
                  )}
                  {t.status === 'failed' && (
                    <Chip size="small" label="Failed" color="error" />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {(t.artists || []).join(' × ') || 'No artists listed'}
                  {t.duration_seconds ? ` · ${t.duration_seconds}s` : ''}
                  {t.created_at ? ` · ${new Date(t.created_at).toLocaleString()}` : ''}
                </Typography>

                {t.status === 'complete' && t.mp3_url && (
                  <Box sx={{ mt: 1.5 }}>
                    <audio controls src={t.mp3_url} style={{ width: '100%', maxWidth: 480 }}>
                      Your browser doesn't support audio playback.
                    </audio>
                  </Box>
                )}

                {t.status === 'failed' && t.error_message && (
                  <Alert severity="error" sx={{ mt: 1 }}>{t.error_message}</Alert>
                )}

                <Collapse in={!!expanded[t.uuid]}>
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#f7f7f7', borderRadius: 1 }}>
                    {t.vibe && (
                      <>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>VIBE</Typography>
                        <Typography variant="body2" sx={{ mb: 1.5 }}>{t.vibe}</Typography>
                      </>
                    )}
                    {t.about && (
                      <>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>ABOUT</Typography>
                        <Typography variant="body2" sx={{ mb: 1.5 }}>{t.about}</Typography>
                      </>
                    )}
                    {t.lyrics && (
                      <>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>LYRICS</Typography>
                        <Typography variant="body2" component="pre" sx={{
                          fontFamily: 'inherit',
                          whiteSpace: 'pre-wrap',
                          mt: 0.5,
                        }}>
                          {t.lyrics}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Collapse>
              </Box>

              <Stack direction="column" spacing={0.5}>
                <Tooltip title={expanded[t.uuid] ? 'Hide details' : 'Show lyrics & details'}>
                  <IconButton size="small" onClick={() => toggleExpand(t.uuid)}>
                    {expanded[t.uuid] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Tooltip>
                {t.status === 'complete' && t.mp3_url && (
                  <Tooltip title="Download MP3">
                    <IconButton
                      size="small"
                      component="a"
                      href={t.mp3_url}
                      download={`${t.title.replace(/[^a-z0-9]+/gi, '_')}.mp3`}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {t.status === 'complete' && (
                  <Tooltip title={t.is_public ? 'Shared — manage link' : 'Share with anyone'}>
                    <IconButton
                      size="small"
                      onClick={() => openShareDialog(t)}
                      color={t.is_public ? 'primary' : 'default'}
                    >
                      <ShareIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => deleteTrack(t.uuid)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>

      {/* ── Share dialog ──────────────────────────────────────────── */}
      <Dialog open={!!shareTrack} onClose={closeShareDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Share “{shareTrack?.title}”
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Turn on public sharing to let anyone with the link listen to this
            track and download the MP3. No login required.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={!!shareTrack?.is_public}
                onChange={(e) => toggleShare(e.target.checked)}
                disabled={shareSaving}
              />
            }
            label={shareTrack?.is_public ? 'Public link is ON' : 'Public link is OFF'}
          />

          {shareTrack?.is_public && (
            <Box sx={{ mt: 2 }}>
              <TextField
                id="soundcheck-share-url-input"
                value={shareTrack ? buildShareUrl(shareTrack.uuid) : ''}
                fullWidth
                size="small"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                        <IconButton onClick={copyShareLink} edge="end" size="small">
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                onFocus={(e) => e.target.select()}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Anyone with this link can play and download the MP3. Turn the
                switch off any time to revoke access.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeShareDialog}>Close</Button>
          {shareTrack?.is_public && (
            <Button
              variant="contained"
              onClick={copyShareLink}
              startIcon={<ContentCopyIcon />}
            >
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );

  // Anonymous visitors get a lightweight public shell so /soundcheck looks
  // like a real product page instead of a bare form floating on white.
  // Logged-in users skip this — App.js already wraps them in <Layout>.
  if (!isAnon) return content;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <Box sx={{
        borderBottom: '1px solid #e8edf2',
        bgcolor: '#fff',
        py: 2.5,
        px: { xs: 2, sm: 4 },
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}
          sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.3px', lineHeight: 1.1 }}>
              Audio Command Systems
            </Typography>
            <Typography variant="caption" sx={{ color: '#8898A4' }}>
              Soundcheck — AI music, on demand
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            href="/login"
            sx={{ borderColor: '#0D2C54', color: '#0D2C54' }}
          >
            ACS team sign in
          </Button>
        </Stack>
      </Box>
      {content}
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="caption" sx={{ color: '#8898A4' }}>
          © {new Date().getFullYear()} Audio Command Systems
        </Typography>
      </Box>
    </Box>
  );
}
